import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Upload, Download, ArrowUpAZ, ArrowDownAZ } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { CustomerImport } from "../components/CustomerImport";
import { CustomerRates } from "../components/CustomerRates";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PageHeader, DataTable, Td, FormDialog, Field, TextInput, SelectInput, ActionButton, EmptyState } from "../components/common";

const EMPTY = { name: "", contact_name: "", requestor: "", email: "", phone: "", address: "", notes: "", requires_stages: false };

const SORT_FIELDS = [
  { value: "name", label: "Company" },
  { value: "contact_name", label: "Contact" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "address", label: "Address" },
];

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [exportingIds, setExportingIds] = useState(false);

  const load = () => api.get("/customers").then((r) => setCustomers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportIds = async () => {
    if (exportingIds) return;
    setExportingIds(true);
    try {
      const { data } = await api.get("/customers/export.csv", { responseType: "blob" });
      downloadFile(data, "customer_ids.csv");
      toast.success("Customer ID download started");
    } catch (e) {
      toast.error("Could not download customer IDs. Please try again.");
    } finally {
      setExportingIds(false);
    }
  };

  const exportCsv = () => {
    const headers = ["id", "name", "contact_name", "requestor", "email", "phone", "address", "notes", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...sortedCustomers.map((c) => headers.map((h) => esc(c[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    downloadFile(blob, "customers_export.csv");
    toast.success(`Exported ${customers.length} customers`);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    const av = a[sortBy];
    const bv = b[sortBy];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = String(av).localeCompare(String(bv));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || "", contact_name: c.contact_name || "", requestor: c.requestor || "", email: c.email || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "", requires_stages: !!c.requires_stages });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, payload);
        toast.success(`Customer ${form.name} updated`);
      } else {
        await api.post("/customers", payload);
        toast.success(`Customer ${form.name} added`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const customerForm = (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Company name">
        <TextInput testId="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Contact person">
          <TextInput testId="customer-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
        </Field>
        <Field label="Requester">
          <TextInput testId="customer-requestor-input" value={form.requestor} onChange={(e) => setForm({ ...form, requestor: e.target.value })} placeholder="Who requests service" />
        </Field>
      </div>
      <Field label="Email">
        <TextInput testId="customer-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Field>
      <Field label="Phone">
        <TextInput testId="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label="Address">
        <TextInput testId="customer-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </Field>
      <Field label="Notes">
        <TextInput testId="customer-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div className="rounded-md border border-slate-700 bg-slate-800/40 px-3 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-200">Use well stages</p>
            <p className="text-xs text-slate-500 mt-0.5">Track A/B/C stages on this customer's rental tickets and bill per stage.</p>
          </div>
          <button
            type="button"
            data-testid="customer-requires-stages-toggle"
            aria-pressed={form.requires_stages}
            onClick={() => setForm({ ...form, requires_stages: !form.requires_stages })}
            className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono uppercase tracking-wider transition-colors ${form.requires_stages ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200" : "bg-slate-900/60 border-slate-600 text-slate-400"}`}
          >
            <span className={`inline-flex h-4 w-8 items-center rounded-full transition-colors ${form.requires_stages ? "bg-indigo-500" : "bg-slate-600"}`}>
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${form.requires_stages ? "translate-x-4" : "translate-x-1"}`} />
            </span>
            {form.requires_stages ? "Yes" : "No"}
          </button>
        </div>
      </div>
      <ActionButton testId="customer-submit-button" type="submit" disabled={busy} className="w-full justify-center">
        {busy ? "Saving…" : editing ? "Save Changes" : "Add Customer"}
      </ActionButton>
    </form>
  );

  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle="Shared customer ledger used by every module" testId="customers-header">
        <ActionButton type="button" testId="export-customer-ids-button" variant="secondary" onClick={exportIds} disabled={exportingIds}>
          <Download size={16} /> {exportingIds ? "Downloading…" : "Export Customer IDs"}
        </ActionButton>
        <ActionButton testId="export-customers-button" variant="secondary" onClick={exportCsv}>
          <Download size={16} /> Export CSV
        </ActionButton>
        <ActionButton testId="import-customers-button" variant="secondary" onClick={() => setImportOpen(true)}>
          <Upload size={16} /> Import CSV
        </ActionButton>
        <ActionButton testId="create-customer-button" onClick={openCreate}>
          <Plus size={16} /> Add Customer
        </ActionButton>
      </PageHeader>

      <div className="flex items-center gap-3 mb-4" data-testid="customer-sort-controls">
        <span className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400">Sort by</span>
        <SelectInput
          testId="customer-sort-field"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          options={SORT_FIELDS}
          className="!w-44 !bg-white !text-slate-100"
        />
        <button
          data-testid="customer-sort-direction"
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors duration-200"
        >
          {sortDir === "asc" ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />}
          {sortDir === "asc" ? "A → Z" : "Z → A"}
        </button>
      </div>

      {customers.length === 0 ? (
        <EmptyState message="No customers yet" testId="customers-empty" />
      ) : (
        <DataTable
          testId="customers-table"
          columns={["Company", "Contact", "Email", "Phone", "Stages", ""]}
          rows={sortedCustomers}
          renderRow={(c) => (
            <tr key={c.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`customer-row-${c.id}`}>
              <Td className="font-medium text-slate-100">{c.name}</Td>
              <Td>{c.contact_name || "—"}</Td>
              <Td className="text-slate-400">{c.email || "—"}</Td>
              <Td className="text-slate-400">{c.phone || "—"}</Td>
              <Td>
                {c.requires_stages ? (
                  <span data-testid={`customer-stages-yes-${c.id}`} className="px-2 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-[10px] font-mono uppercase">Yes</span>
                ) : (
                  <span data-testid={`customer-stages-no-${c.id}`} className="px-2 py-0.5 rounded bg-slate-700/40 border border-slate-600 text-slate-400 text-[10px] font-mono uppercase">No</span>
                )}
              </Td>
              <Td>
                <div className="flex gap-1">
                  <button data-testid={`customer-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors duration-200">
                    <Pencil size={14} />
                  </button>
                </div>
              </Td>
            </tr>
          )}
        />
      )}

      <CustomerImport open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Customer" : "Add Customer"} testId="customer-form-dialog" className={editing ? "max-w-2xl" : "max-w-lg"}>
        {editing ? (
          <Tabs defaultValue="details">
            <TabsList data-testid="customer-dialog-tabs">
              <TabsTrigger value="details" data-testid="customer-tab-details">Details</TabsTrigger>
              <TabsTrigger value="rates" data-testid="customer-tab-rates">Rates &amp; Services</TabsTrigger>
            </TabsList>
            <TabsContent value="details">{customerForm}</TabsContent>
            <TabsContent value="rates"><CustomerRates customerId={editing.id} /></TabsContent>
          </Tabs>
        ) : customerForm}
      </FormDialog>
    </div>
  );
}
